import { useEffect, useRef, useState, type ReactNode } from "react";
import { Bot, Check, ChevronRight, CircleStop, LoaderCircle } from "lucide-react";
import { Button } from "@opencloud/ui";
import { api } from "../api.js";
import { childAgentName, childFinishedAgo, childStateLabel, conversationChildAgents } from "../child-agent-presentation.js";
import { projectChildAgentEvidence } from "../child-agent-evidence.js";
import { conversationTaskLists } from "../task-presentation.js";
import type { AgentTraceInspectionView, ChatSnapshot as ConversationSnapshot, IncidentView } from "../types.js";
import { ChildAgentPanel, type AgentDetailPart } from "./ChildAgentPanel.js";
import { traceInspectionPath } from "./TraceInspector.js";

export function assignmentRuns(snapshot: ConversationSnapshot) {
  return snapshot.runs.filter(run => run.task || run.trigger === "incident" || run.parentRunId);
}

export function AssignmentBriefs({ snapshot, incidents, onlyRunId }: { snapshot: ConversationSnapshot; incidents: readonly IncidentView[]; onlyRunId?: string }) {
  const appId = snapshot.app?.id;
  if (!appId) return null;
  const native = conversationChildAgents(snapshot);
  const plans = conversationTaskLists(snapshot);
  return <>{assignmentRuns(snapshot).filter(run => !onlyRunId || run.id === onlyRunId).map(run => {
    const incident = incidents.find(incident => incident.runId === run.id);
    const child = native.find(part => part.data.runId === run.id);
    const state = run.status === "queued" ? "queued" : run.status === "completed" ? "succeeded"
      : run.status === "failed" ? "failed" : run.status === "cancelled" ? "cancelled"
      : ["paused", "waiting_for_user"].includes(run.status) ? "waiting" : "active";
    const part: AgentDetailPart = child ?? { type: "data-child", id: `assignment:${run.id}`, data: {
      runId: run.id, parentRunId: null, purpose: run.task?.title ?? incident?.title ?? "Automatic repair",
      state, outcome: null, occurredAt: run.createdAt, updatedAt: run.updatedAt,
    } };
    const message = [...snapshot.messages].reverse().find(message => message.runId === run.id && message.role === "agent");
    const plan = plans.find(plan => plan.runId === run.id)?.taskList;
    const status = run.task ? ({ queued: "Queued", running: "Working", processing: "Saving results",
      succeeded: "Finished", partial: "Partly finished", failed: "Needs attention", stopped: "Stopped" }[run.task.state])
      : incident ? incident.status === "repaired" ? "Repaired"
        : state === "succeeded" ? "Checking repair" : childStateLabel(state)
        : childStateLabel(part.data.state);
    return <AssignmentBrief key={run.id} appId={appId} part={part}
      threadId={snapshot.thread.id}
      detailSummary={incident ? <section className="a3-assignment-detail-summary" aria-label="Repair evidence">
        <h3>Alert details</h3><p>{incident.summary}</p><p>{incident.policy.reason}</p>
        <h3>Recovery evidence</h3><p>{incident.status === "repaired" ? "Recovery verified" : "Recovery has not been verified."}</p>
        <p>{incident.repairEvidence.length ? `${incident.repairEvidence.length} retained checks: ${[...new Set(incident.repairEvidence.map(item => item.split(":")[0]))].join(", ")}` : "No recovery checks recorded yet."}</p>
        {incident.suppressionReason && <p>{incident.suppressionReason}</p>}
      </section> : run.task ? <section className="a3-assignment-detail-summary" aria-label="Saved results">
        <p>{run.task.completed} of {run.task.total} assignments have confirmed saved results.</p>
        <p>Stopping preserves results already saved. Accepted Function operations may still finish.</p>
      </section> : undefined}
      source={run.task ? "App automation" : incident ? "Triggered by an error alert" : "Assigned by the main agent"}
      status={status} message={message ? { text: message.content, at: message.createdAt } : null}
      todo={plan?.items.length ? { completed: plan.items.filter(item => item.status === "completed").length,
        total: plan.items.length, current: plan.items.find(item => item.status === "in_progress")?.label
          ?? plan.items.find(item => item.status === "pending")?.label ?? "Tasks complete" } : null} />;
  })}</>;
}

export function AssignmentBrief({ appId, threadId, part, source, status, message, todo, detailSummary }: {
  appId: string; part: AgentDetailPart; source: string; status: string;
  threadId?: string; detailSummary?: ReactNode;
  message: { text: string; at: string } | null;
  todo: { current: string; completed: number; total: number } | null;
}) {
  const [open, setOpen] = useState(false);
  const [retainedMessage, setRetainedMessage] = useState(message);
  const [retainedTodo, setRetainedTodo] = useState(todo);
  const trigger = useRef<HTMLButtonElement>(null);
  const [stopping, setStopping] = useState(false);
  const [stopError, setStopError] = useState<string | null>(null);
  const stop = async () => {
    setStopping(true); setStopError(null);
    try {
      await api(`/v1/apps/${encodeURIComponent(appId)}/threads/${encodeURIComponent(threadId!)}/runs/${encodeURIComponent(part.data.runId)}/cancel`, { method: "POST" });
    } catch { setStopError("Could not stop this run. Try again."); setStopping(false); }
  };
  const working = part.data.state === "active";
  const title = childAgentName(part.data.purpose);
  // Native children's messages live in their protected trace lane. Use the
  // same authorized projection as details, and never substitute a tool result.
  useEffect(() => {
    if (!part.data.parentRunId) return;
    const controller = new AbortController();
    let inFlight = false;
    const load = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        let inspection = await api<AgentTraceInspectionView>(traceInspectionPath(appId, part.data.parentRunId!, 0, 100, part.data.runId), { signal: controller.signal });
        if (inspection.window.total > 100) inspection = await api<AgentTraceInspectionView>(
          traceInspectionPath(appId, part.data.parentRunId!, inspection.window.total - 100, 100, part.data.runId), { signal: controller.signal });
        const evidence = projectChildAgentEvidence(inspection.entries);
        const latest = evidence.timeline.filter(item => item.kind === "message").at(-1);
        if (!controller.signal.aborted && evidence.todo) setRetainedTodo(evidence.todo);
        if (!controller.signal.aborted && latest?.kind === "message") setRetainedMessage({ text: latest.text, at: latest.occurredAt });
      } catch { /* Keep the last confirmed message during a connection gap. */ }
      finally { inFlight = false; }
    };
    void load();
    const timer = working ? window.setInterval(() => void load(), 15_000) : null;
    return () => { controller.abort(); if (timer !== null) window.clearInterval(timer); };
  }, [appId, part.data.parentRunId, part.data.runId, working]);
  const latest = part.data.parentRunId ? retainedMessage ?? message : message;
  if (part.data.parentRunId) todo = retainedTodo ?? todo;
  return <>
    <Button ref={trigger} variant="outline" className="a3-assignment-brief" aria-haspopup="dialog"
      aria-label={`${title}. ${status}. Open sub-agent details`} onClick={() => setOpen(true)}>
      <span className="a3-assignment-header"><Bot aria-hidden="true" />
        <span className="a3-assignment-heading"><strong>{title}</strong><span>Sub-agent · {source}</span></span>
        <span className="a3-assignment-status">{working ? <LoaderCircle className="a3-spin" /> : part.data.state === "succeeded" ? <Check /> : <CircleStop />}{status}</span>
        <ChevronRight aria-hidden="true" />
      </span>
      {todo && <span className="a3-assignment-todo"><span><span>{todo.current}</span><span>{todo.completed} of {todo.total}</span></span>
        <span className="a3-assignment-progress" role="progressbar" aria-label="Sub-agent todo progress" aria-valuemin={0} aria-valuemax={todo.total} aria-valuenow={todo.completed}>
          <span style={{ width: `${todo.completed / todo.total * 100}%` }} />
        </span></span>}
      <span className="a3-assignment-update"><span className="a3-assignment-update-heading">LATEST UPDATE
        {latest && <time dateTime={latest.at}> · {childFinishedAgo(latest.at, Date.now())}</time>}</span>
        <span className="a3-assignment-last-message">{latest?.text ?? "No update yet."}</span>
      </span>
    </Button>
    <ChildAgentPanel appId={appId} part={part} open={open} onOpenChange={setOpen} returnFocus={trigger}
      stateLabel={status} stateDetail={source} summary={<>{detailSummary}
        {threadId && !part.data.parentRunId && ["queued", "active", "waiting"].includes(part.data.state) &&
          <Button variant="outline" disabled={stopping} onClick={() => void stop()}>{stopping ? "Stopping…" : "Stop sub-agent"}</Button>}
        {stopError && <p role="alert">{stopError}</p>}</>} />
  </>;
}
